import { LW_REFINE_QUERY_PARAM } from '../SearchResultsFacets/constants';
import {
    ExtendedSearchResult,
    ExtendedSearchResultGRS,
    ExtendedSearchResultLW,
} from '../SearchResultsGrid/types';

import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { PageTypes } from '@/constants/index';
import { SearchServiceProps } from '@/services/search/types';
import { PageType } from '@/types/pages/search';
import {
    useAuth,
    useBrowseContext,
} from '@nxt-costco-com/forge-digital-components';

import { stringifyKeyword } from './SearchPageDigitalDataLayer';
import {
    CATEGORY,
    CONTENTSTACK,
    ENV_NONPROD,
    ENV_PROD,
    REACT,
    SEARCH,
    TRAVEL_DESTINATION,
    TRAVEL_ITEM_PID,
} from './constants';

const SearchAnalytics = memo(
    ({
        searchResults,
        searchService,
        pageType,
        categoryTitle,
        site,
        lang,
        resultsPerPage,
        pageCrumbs,
        attributionToken,
        correctedSearchTerm,
        sortType,
        fusionQueryId,
        xFusionExitCode,
        searchType,
        grsFilterEvent,
        pageCategories,
        searchParams,
    }: {
        searchResults: ExtendedSearchResult[] | undefined;
        searchService: SearchServiceProps;
        pageType: PageType;
        categoryTitle?: string;
        site: string;
        lang: string;
        resultsPerPage: number;
        pageCrumbs: string[];
        attributionToken: string;
        correctedSearchTerm: string;
        sortType?: string;
        fusionQueryId: string;
        xFusionExitCode: string;
        searchType: string | null;
        grsFilterEvent: {
            isFilterOnlyEvent?: boolean;
            filters?: string[];
        };
        pageCategories: string[];
        searchParams: URLSearchParams | null;
    }) => {
        const { isUserSignedIn, isLoading: isAuthUtilsLoading } = useAuth();
        const pageLoadEventRef = useRef<string | null>(null);
        const [isUserLoggedIn, setIsUserLoggedIn] = useState<
            boolean | undefined
        >(undefined);
        const {
            // warehouse,
            deliveryLocation: bcDeliveryLocation,
            bdDeliveryLocation,
            userLocation: bcUserLocation,
            bdWarehouseNumber: bdUserLocation,
        } = useBrowseContext();
        const isBD = site?.endsWith('BD');
        const deliveryLocation = isBD ? bdDeliveryLocation : bcDeliveryLocation;
        const userLocation = isBD
            ? `${bdUserLocation}`?.split('|')?.[0]
            : bcUserLocation?.city;
        const deliveryZip =
            deliveryLocation?.zipCode?.replace?.('+', ' ') || 'na';
        const currentPageNumber = searchParams?.get('currentPage') || '1';
        const keywordParam = stringifyKeyword(
            searchParams?.get('keyword') || '',
        );
        const pageName =
            pageType === PageTypes.SEARCH
                ? 'Search'
                : `Category | ${categoryTitle}`;
        const isFilterOnlyEvent = Boolean(grsFilterEvent?.isFilterOnlyEvent);

        // Memoize filter array to prevent unnecessary useEffect re-runs
        const filter = useMemo(() => {
            if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
                return [...(grsFilterEvent.filters || [])];
            } else {
                const refineParam = searchParams?.get(LW_REFINE_QUERY_PARAM);
                return refineParam ? [refineParam] : [];
            }
        }, [searchService, grsFilterEvent, searchParams]);

        useEffect(() => {
            if (!isAuthUtilsLoading) {
                isUserSignedIn().then(result => {
                    setIsUserLoggedIn(Boolean(result));
                });
            }
        }, [isAuthUtilsLoading, isUserSignedIn]);

        useEffect(() => {
            const shouldLoadEventString = JSON.stringify({
                isUserLoggedIn,
                isAuthUtilsLoading,
                searchResults,
                searchService,
                sortType,
            });
            if (pageLoadEventRef.current !== shouldLoadEventString) {
                const sessionStatus = isUserLoggedIn
                    ? 'Authenticated'
                    : 'Unauthenticated';

                if (window) {
                    const siteReleaseEnv =
                        process?.env
                            ?.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION &&
                        process?.env?.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION.toLowerCase() ===
                            ENV_PROD
                            ? ENV_PROD
                            : ENV_NONPROD;
                    const [siteLang, siteCountry] = lang.split('-');
                    const siteStore = site.slice(-2).toLowerCase();
                    const products = [];

                    // Gather product information in required format from search results
                    let nativeProducts = 0;
                    for (let i = 0; i < (searchResults?.length || 0); i++) {
                        const currentSearchResult = searchResults?.[i];
                        if (!currentSearchResult) {
                            continue;
                        }
                        if (currentSearchResult?.isCriteoProductTileAd) {
                            continue;
                        } else if (
                            currentSearchResult?.isCriteoButterflyAd ||
                            currentSearchResult?.isCriteoButterflyFiller
                        ) {
                            continue;
                        } else {
                            const isTravelItem =
                                !!(
                                    currentSearchResult as ExtendedSearchResultLW
                                )?.content_type?.includes(TRAVEL_DESTINATION) ||
                                !!(
                                    currentSearchResult as ExtendedSearchResultGRS
                                )?.product?.attributes[
                                    'product_type'
                                ]?.text?.[0]?.includes(TRAVEL_DESTINATION);

                            products.push({
                                pid: isTravelItem
                                    ? TRAVEL_ITEM_PID
                                    : (
                                          currentSearchResult as ExtendedSearchResultLW
                                      ).group_id ||
                                      (
                                          currentSearchResult as ExtendedSearchResultGRS
                                      ).id,
                                sku: isTravelItem
                                    ? TRAVEL_ITEM_PID
                                    : (
                                          currentSearchResult as ExtendedSearchResultLW
                                      ).item_number ||
                                      ((
                                          currentSearchResult as ExtendedSearchResultGRS
                                      ).variantRollupValues?.variantId?.[0]?.toString() ??
                                          ''),
                                searchDisplayRank: nativeProducts++,
                            });
                        }
                    }

                    window.digitalData = {
                        ...window.digitalData,
                        authStatus: sessionStatus,
                        actualSearchTerm: keywordParam,
                        ...(attributionToken ? { attributionToken } : {}),
                        ...(fusionQueryId ? { fusionQueryId } : {}),
                        ...(xFusionExitCode ? { xFusionExitCode } : {}),
                        experimentIds: [searchService || 'def'],
                        ...(searchService === SEARCH_SERVICE_PROVIDERS.GRS
                            ? {
                                  isFilterOnlyEvent,
                              }
                            : {}),
                        pageCategories,
                        deliveryZip,
                        localWH: userLocation || 'na',
                        orderBy: sortType,
                        pageCMS: CONTENTSTACK,
                        pagePlatform: REACT,
                        pageType:
                            pageType === PageTypes.SEARCH ? SEARCH : CATEGORY,
                        filter,
                        pageName: pageName,
                        searchTerm: correctedSearchTerm || keywordParam,
                        ...(searchType ? { searchType } : {}),
                        siteCountry,
                        siteLang: `${siteLang}-${siteCountry?.toUpperCase()}`,
                        siteStore,
                        siteReleaseEnv,
                        searchProvider: searchService?.toUpperCase() || 'DEF',
                        entity:
                            'costco-' +
                            siteStore.split('').join('2') +
                            '-' +
                            siteCountry,
                        products,
                        pageOffset:
                            (parseInt(currentPageNumber, 10) - 1) *
                            resultsPerPage,
                        pageCrumbs,
                    };
                }

                // Because this useEffect watches isUserLoggedIn, we need to ensure that
                // we don't push multiple page load events as auth state is determined
                if (!isAuthUtilsLoading && isUserLoggedIn !== undefined) {
                    window.digitalDataEvents = window.digitalDataEvents || [];
                    window.digitalDataEvents.push({
                        pageView: '1',
                    });
                    pageLoadEventRef.current = shouldLoadEventString;
                }
            }
        }, [
            isUserLoggedIn,
            isAuthUtilsLoading,
            deliveryZip,
            userLocation,
            pageType,
            site,
            lang,
            searchResults,
            searchService,
            currentPageNumber,
            resultsPerPage,
            pageName,
            pageCrumbs,
            keywordParam,
            attributionToken,
            correctedSearchTerm,
            sortType,
            fusionQueryId,
            xFusionExitCode,
            searchType,
            filter,
            isFilterOnlyEvent,
            pageCategories,
        ]);

        return null;
    },
);

SearchAnalytics.displayName = 'SearchAnalytics';

export default SearchAnalytics;
